import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LangToggle } from "../components/LangToggle";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button, Card, Input } from "../components/ui";
import { PASSWORD_MIN_LENGTH } from "../../shared/password-rules";
import { PasswordStrength } from "../components/PasswordStrength";
import { useT } from "../i18n";
import { passwordErrorText } from "../i18n/errors";
import { authClient } from "../lib/auth";
import { usePasswordCheck } from "../lib/password";

/**
 * Landet man über den Link aus der „Passwort vergessen“-Mail hier
 * (/reset-password?token=…). Ungültige/abgelaufene Links kommen mit ?error an.
 */
export function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const token = params.get("token");
  const linkInvalid = !token || params.get("error") !== null;

  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  // Richtlinien-Verstoß: Formular bleibt stehen, nur der Hinweis erscheint
  const [policyError, setPolicyError] = useState<string | null>(null);
  const check = usePasswordCheck(password);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setFailed(false);
    setPolicyError(null);
    const { error } = await authClient.resetPassword({ newPassword: password, token });
    if (error) {
      const text = passwordErrorText(t, error.code);
      if (text) setPolicyError(text);
      else setFailed(true);
    } else setDone(true);
    setBusy(false);
  }

  return (
    <div className="relative grid min-h-dvh place-items-center px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LangToggle />
        <ThemeToggle />
      </div>
      <main className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            fast<span className="text-accent">360</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.login.tagline}</p>
        </div>
        <Card>
          {linkInvalid || failed ? (
            <div className="space-y-4">
              <p className="text-sm text-red-600 dark:text-red-400">
                {failed ? t.reset.failed : t.reset.invalid}
              </p>
              <Button className="w-full" onClick={() => navigate("/", { replace: true })}>
                {t.reset.requestNew}
              </Button>
            </div>
          ) : done ? (
            <div className="space-y-4">
              <p className="text-sm text-emerald-600 dark:text-emerald-400">{t.reset.success}</p>
              <Button className="w-full" onClick={() => navigate("/", { replace: true })}>
                {t.reset.toLogin}
              </Button>
            </div>
          ) : (
            <form onSubmit={(e) => void submit(e)} className="space-y-4">
              <h2 className="text-sm font-semibold">{t.reset.title}</h2>
              <Input
                type="password"
                className="w-full"
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.reset.passwordPlaceholder(PASSWORD_MIN_LENGTH)}
              />
              <PasswordStrength password={password} result={check} />
              {policyError && (
                <p className="text-sm text-red-600 dark:text-red-400">{policyError}</p>
              )}
              <Button type="submit" className="w-full" disabled={busy || !!check?.rejection}>
                {busy ? "…" : t.reset.submit}
              </Button>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}
